import {Box, Button, Typography} from '@mui/material';
import './ProcessingSyncDetailsPanel.scss';
import {t} from 'i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import {useState, useRef, useMemo} from 'react';
import {type GridColDef, type GridRenderCellParams} from '@mui/x-data-grid';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import {ReactComponent as MoreIcon} from '../../../../assets/images/icon-more.svg';
import {IProcSyncRecordsResponse} from '../../../../api/types/ingestionApi.type';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import {
  useProcSyncRecords,
  useProcSyncRetry,
  useProcSyncSummary,
} from './hooks/useProcSync';

export const TEST_IDS = {
  CONTAINER: 'processing-sync-errors-panel',
  REFRESH_BUTTON: 'errors-refresh-button',
  TABLE: 'errors-table',
  NO_ERRORS_ROW: 'no-errors-row',
  PAGINATION: 'errors-pagination',
} as const;

interface ProcessingSyncDetailsPanelProps {
  onRefresh?: () => void;
  onRetry?: (error: IProcSyncRecordsResponse) => void;
}

const ProcessingSyncDetailsPanel = ({
  onRefresh,
  onRetry,
}: ProcessingSyncDetailsPanelProps) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedError, setSelectedError] =
    useState<IProcSyncRecordsResponse | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const menuRef = useRef<ArrMenuHandle | null>(null);

  const HOSTING_PLATFORM = 'Sightline';
  const {data, isLoading, isError, refetch} = useProcSyncRecords({
    pageNumber: 1,
    pageSize: 1000,
    sortBy: 'Timestamp',
    sortOrder: 'Desc',
  });

  const {mutate: retryRecord} = useProcSyncRetry();
  const {data: summaryData} = useProcSyncSummary();

  const allRecords = useMemo(() => data ?? [], [data]);
  const totalRecords = allRecords.length;
  const hasError = (err: IProcSyncRecordsResponse) => !err.isSaved;
  const hcode = summaryData?.hCode ?? '';
  const objectType = selectedError?.rdoType;
  const objectId = selectedIndex;

  const formatDate = (input: string): string => {
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return 'Invalid Date';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    error: IProcSyncRecordsResponse,
    index: number
  ) => {
    menuRef.current?.open(e.currentTarget as HTMLElement);
    setSelectedError(error);
    setSelectedIndex(index);
  };

  const handleRefresh = async () => {
    setPage(1);
    await refetch();
    onRefresh?.();
  };

  const getMenuItems = (
    error: IProcSyncRecordsResponse | null
  ): CustomMenuItem[] => [
    {
      id: 'retry',
      label: 'Retry',
      disabled: !error || error.isSaved,
    },
    {
      id: 'dismiss',
      label: 'Dismiss',
      disabled: !error || error.isSaved,
    },
  ];

  const onMenuItemClick = (item: CustomMenuItem) => {
    menuRef.current?.close();

    setTimeout(() => {
      if (!selectedError) return;
      if (!hasError(selectedError)) return;
      if (item.id === 'retry') {
        retryRecord(
          {
            projectCode: hcode,
            hostingPlatform: HOSTING_PLATFORM,
            objectType: objectType ?? '',
            objectId: objectId ?? 0,
          },
          {
            onSuccess: (response) => {
              console.log('Retry successful:', response.message);
              refetch();
              onRetry?.(selectedError);
            },
            onError: (error) => {
              console.error('Retry failed:', error);
            },
          }
        );
      }
    }, 0);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'errors',
        headerName: t('ingestions.mapped.processingsync.errorColumn'),
        flex: 1,
      },
      {
        field: 'rdoType',
        headerName: t('ingestions.mapped.processingsync.rdoColumn'),
        flex: 1,
      },
      {
        field: 'sourceID',
        headerName: t('ingestions.mapped.processingsync.sourceIdColumn'),
        flex: 1,
      },
      {
        field: 'lastUpdatedOn',
        headerName: t('ingestions.mapped.processingsync.timestampColumn'),
        flex: 1,
        renderCell: (params: GridRenderCellParams<IProcSyncRecordsResponse>) =>
          formatDate(params.value as string),
      },
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        hideSortIcons: true,
        disableColumnMenu: true,
        resizable: false,
        valueGetter: () => '',
        renderCell: (
          params: GridRenderCellParams<IProcSyncRecordsResponse>
        ) => {
          const err = params.row as IProcSyncRecordsResponse;
          const rowIndex = params.api.getRowIndexRelativeToVisibleRows(
            params.id
          );
          return (
            <ArrIconButton
              onClick={(e) => handleMoreIconClick(e, err, rowIndex)}
              icon={<MoreIcon />}
              ariaLabel="More actions"
              shape="none"
              testId={`error-more-action-btn-${err.sourceID}`}
              className="error-grid-icon-button"
              disabled={!hasError(err)}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <Box
      className="processing-sync-errors-panel"
      data-testid={TEST_IDS.CONTAINER}
    >
      <Box className="panel-header-row">
        <Typography
          className="typo-text subhead2_size panel-section-title"
          data-testid="errors-section-heading"
        >
          {t('ingestions.mapped.processingsync.details')}
        </Typography>
        <Button
          className="refresh-button"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          data-testid={TEST_IDS.REFRESH_BUTTON}
        >
          {t('ingestions.mapped.processingsync.refresh')}
        </Button>
      </Box>

      {isError && (
        <Box
          className="errors-panel-error"
          role="alert"
          data-testid="errors-panel-error"
        >
          <ErrorOutlineIcon data-testid="errors-panel-error-icon" />
          <Typography
            className="typo-text body3_size"
            data-testid="errors-panel-error-message"
          >
            {t('ingestions.mapped.processingsync.loadError')}
          </Typography>
        </Box>
      )}

      <ArrDataGrid
        className="errors-table"
        data-testid={TEST_IDS.TABLE}
        rows={allRecords}
        columns={columns}
        primaryKey="sourceID"
        loading={isLoading}
        rowCount={totalRecords}
        currentPage={page}
        setCurrentPage={setPage}
        itemsPerPage={pageSize}
        setItemsPerPage={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        showPerPageOptions={true}
        pageSizeOptions={[25, 50, 100]}
        entityType={t('ingestions.mapped.processingsync.entries')}
        alwaysShowPagination={true}
        noRowsOverlayCustomText={t(
          'ingestions.mapped.processingsync.noRecords'
        )}
        paginationMode="client"
        paginationModel={{page: page - 1, pageSize}}
        sx={{
          '& .MuiDataGrid-cellEmpty': {display: 'none'},
          '& .MuiDataGrid-columnSeparator': {display: 'none'},
        }}
      />

      <ArrMenu
        id="error-row-menu"
        ref={menuRef}
        items={getMenuItems(selectedError)}
        onItemClick={onMenuItemClick}
        direction="left"
      />
    </Box>
  );
};

export default ProcessingSyncDetailsPanel;
